import { ComponentPropsWithoutRef, forwardRef, Ref, useContext } from "react";
import cx from "classnames";
import styles from "./Page.module.scss";
import { PageSideBarContext } from "./PageSidebarContext";

/**
 * Props for the PageSidebarHeader component
 * @extends ComponentPropsWithoutRef<"div">
 */
export type PageSidebarHeaderProps = ComponentPropsWithoutRef<"div">;

/**
 * PageSidebarHeader component for creating header sections within sidebars.
 *
 * Features:
 * - Container for sidebar header content
 * - Automatic visibility handling based on sidebar collapsed state
 * - Supports all standard div props
 * - Context-aware styling
 *
 * @example
 * <Page.SidebarHeader>
 *   <Text variant="headline" el="h2" size="medium">
 *     Sidebar Title
 *   </Text>
 * </Page.SidebarHeader>
 */
export const PageSidebarHeader = forwardRef(
  (props: PageSidebarHeaderProps, ref: Ref<HTMLDivElement>) => {
    const { children, className, ...rest } = props;
    const context = useContext(PageSideBarContext);
    const classNames = cx([styles["sidebar-header"]], className);

    return (
      <div className={classNames} ref={ref} {...rest}>
        <div
          className={styles["header-content"]}
          aria-hidden={context?.collapsed}
        >
          {children}
        </div>
      </div>
    );
  },
);

PageSidebarHeader.displayName = "Page.SidebarHeader";
